import { create } from "zustand";
import {
  CAPTURE_HOST_STATES,
  CaptureAnnotation,
  CaptureCardData,
  CaptureHostCapabilities,
  CaptureHostStateName,
  CaptureKind,
  CaptureMediaKind,
  CaptureRequestLocalState,
  CaptureStatus,
  DEFAULT_CAPTURE_INCLUDE,
  DEFAULT_CAPTURE_MAX_RECORDING_SEC,
  captureFileMimeType,
  detectCapturePlatform,
  isCaptureImageDataUrl,
  isFinalCaptureStatus,
  parseCaptureCardData,
} from "../Helper/CaptureRequest";
import {
  getCaptureEditorImageRect,
  makeCaptureFlightImage,
  prefersReducedMotion,
} from "../Helper/CaptureFlight";
import { buildCaptureHostLabels, captureText } from "../Helper/CaptureStrings";
import Communicator from "../Helper/Communicator";
import { CAPTURE_UPLOAD_LIMIT_BYTES } from "../Helper/ConversationAttachmentUpload";
import {
  CaptureClaimResponse,
  HttpCaptureService,
} from "../Services/http.clients/http.capture.client";
import { useConfigStore } from "./Config";
import { AppMode, useRouterStore } from "./Router";
import { useSessionStore } from "./Session";
import { useTicketStore } from "./Ticket";

// A capable host announces itself (`capture-capabilities`) right after the
// ping handshake. Silence past this window = a host that predates capture
// requests (older SDK, or capture disabled) → the card offers upload only.
export const CAPTURE_CAPABILITIES_GRACE_MS = 3000;
// After capture-start the host answers with a capture-state (its bar) at once.
// Nothing at all within this window means it lost the request — say so and
// offer the upload instead of spinning forever.
const HOST_FIRST_RESPONSE_TIMEOUT_MS = 10000;
// A host phase can also go quiet for reasons the host can't report: an iOS
// WebView is suspended while the widget is minimised (its messages queue, the
// final ones may never all arrive), an Android widget re-created mid-capture
// may only get final states. After this long without a host message — with
// the page visible — the card asks the server instead of guessing.
const HOST_SILENCE_CHECK_MS = 10000;
// A watchdog that fires this much later than planned was frozen (suspended
// WebView / background tab): its silence says nothing about the host.
const TIMER_SUSPENSION_SLACK_MS = 2000;
// capture-image can arrive right after a fresh load (Android re-creates the
// widget); the session may still be on its way then.
const SESSION_WAIT_TIMEOUT_MS = 10000;
// Host screenshots are capped at 2560px on the long edge; anything far beyond
// a data URL of this size is not a screenshot we should decode.
const MAX_CAPTURE_DATA_URL_LENGTH = 40 * 1024 * 1024;
// The longest recording any request can ask for (contract: 5..180 s).
const MAX_RECORDING_SEC = 180;
// A host's `done` thumbnail (a small JPEG data: URL) is dropped above this.
const MAX_HOST_THUMBNAIL_LENGTH = 600 * 1024;

const bootedAt = Date.now();

export interface CaptureRequestMeta {
  id: string;
  kind: CaptureKind;
  allowUpload?: boolean;
  annotate?: boolean;
  maxDurationSec?: number;
  audio?: boolean;
  options?: Record<string, any>;
  ticketShareToken?: string;
}

export interface CaptureEditorSession {
  /** Bumped per opened image so a retake remounts the editor fresh. */
  sessionId: number;
  requestId: string;
  dataUrl: string;
  width?: number;
  height?: number;
  annotate: boolean;
  canRetake: boolean;
  method?: string;
  platform?: string;
  sdkType?: string;
  sdkVersion?: string;
  phase: "editing" | "sending" | "error";
  progress?: number;
}

export interface CaptureEditorResult {
  blob: Blob;
  width: number;
  height: number;
  annotations: CaptureAnnotation[];
}

/** The screenshot a native host takes when the widget opens (`screenshot-update`). */
export interface HostScreenshot {
  dataUrl: string;
  /** Date.now() when it arrived. */
  at: number;
}

/** A host screenshot older than this no longer shows "the screen" the customer means. */
export const HOST_SCREENSHOT_MAX_AGE_MS = 30 * 60 * 1000;

interface CaptureState {
  hostCapabilities?: CaptureHostCapabilities;
  /** The last `screenshot-update` from the host (kai-intake CONTRACT §3.2). */
  hostScreenshot?: HostScreenshot;
  setHostScreenshot: (data: any) => void;
  /** "Send it": the host screenshot goes up as the request's image, unedited. */
  sendHostScreenshot: (
    card: CaptureCardData,
    ticketShareToken?: string,
  ) => Promise<void>;
  /** The host screenshot opens in the editor to mark it up first. */
  editHostScreenshot: (
    card: CaptureCardData,
    ticketShareToken?: string,
  ) => Promise<void>;
  /** No capabilities arrived within the grace window. */
  legacyHost: boolean;
  requests: Record<string, CaptureRequestLocalState>;
  meta: Record<string, CaptureRequestMeta>;
  /** The request the host is capturing for right now (one at a time). */
  activeRequestId?: string;
  editor?: CaptureEditorSession;
  applyCapabilities: (data: any) => void;
  /** Resolves a silent host to legacy once the grace window has passed. */
  markLegacyIfUnannounced: () => number;
  registerRequest: (meta: CaptureRequestMeta) => void;
  applyHostState: (data: any) => void;
  handleCaptureImage: (data: any) => void;
  startCapture: (
    card: CaptureCardData,
    kind: CaptureMediaKind,
    ticketShareToken?: string,
  ) => Promise<void>;
  cancelCapture: (requestId: string) => void;
  uploadCaptureFile: (
    card: CaptureCardData,
    file: File,
    ticketShareToken?: string,
  ) => Promise<void>;
  skipRequest: (card: CaptureCardData) => Promise<void>;
  sendEditorResult: (result: CaptureEditorResult) => Promise<void>;
  retakeFromEditor: () => void;
  cancelEditor: () => void;
  /** The host landed the flight (or gave up): the card shows its media. */
  finishFlight: (requestId?: string) => void;
  /**
   * A card first seen "in progress" while nothing on this page works on it:
   * a capture abandoned by a reload or a closed tab. Its claim is released so
   * the team doesn't see "In progress" for a week.
   */
  releaseStaleClaim: (card: CaptureCardData) => void;
  reset: () => void;
}

// ── Module-level helpers (no state of their own until a request arrives) ──

let hostWatchdog:
  | { requestId: string; timer: ReturnType<typeof setTimeout>; armedAt: number }
  | undefined;
let hostSilenceTimer: ReturnType<typeof setTimeout> | undefined;
// Only attached while a request is in a host phase.
let visibilityListener: (() => void) | undefined;
let editorSessionCounter = 0;
const trackedObjectUrls = new Set<string>();
// Cards this page has seen (releaseStaleClaim judges a card once, on first sight).
const seenCaptureCards = new Set<string>();

const clearHostWatchdog = (requestId?: string) => {
  if (!hostWatchdog) {
    return;
  }
  if (requestId && hostWatchdog.requestId !== requestId) {
    return;
  }
  clearTimeout(hostWatchdog.timer);
  hostWatchdog = undefined;
};

const clearHostSilenceTimer = () => {
  if (hostSilenceTimer) {
    clearTimeout(hostSilenceTimer);
    hostSilenceTimer = undefined;
  }
};

const isPageHidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

const isChatbar = () => useRouterStore.getState().appMode === AppMode.CHATBAR;

const DEVICE_ID_KEY = "gleap-capture-device-id";
let memoryDeviceId: string | undefined;

/**
 * A random id for this browser / app install, so the server can tell "claimed
 * on another device" apart from a retake here. Persisted under a Gleap-owned
 * key (see CacheReader) — the iframe shares the host page's storage.
 */
export const getCaptureDeviceId = (): string => {
  if (memoryDeviceId) {
    return memoryDeviceId;
  }
  try {
    const stored = localStorage.getItem(DEVICE_ID_KEY);
    if (stored) {
      memoryDeviceId = stored;
      return stored;
    }
  } catch (_) {}
  const random =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  memoryDeviceId = `messenger-${random}`;
  try {
    localStorage.setItem(DEVICE_ID_KEY, memoryDeviceId);
  } catch (_) {}
  return memoryDeviceId;
};

const trackObjectUrl = (blob: Blob): string | undefined => {
  try {
    const url = URL.createObjectURL(blob);
    trackedObjectUrls.add(url);
    return url;
  } catch (_) {
    return undefined;
  }
};

const reportEvent = (
  requestId: string,
  type: "failed" | "released",
  reason?: string,
) => {
  HttpCaptureService.getInstance()
    .sendEvent(requestId, reason ? { type, reason } : { type })
    .catch(() => {});
};

// Pick up the customer's message and the card's new status even if a
// realtime event was missed while the host had the widget hidden.
const refreshConversationSoon = () => {
  setTimeout(() => {
    const ticketStore = useTicketStore.getState();
    if (ticketStore.currentTicket) {
      ticketStore.loadComments(true).catch(() => {});
    }
  }, 600);
};

const waitForSession = (): Promise<boolean> =>
  new Promise((resolve) => {
    const isReady = () => {
      const { session, sdkKey } = useSessionStore.getState();
      return !!session?.gleapId && !!sdkKey;
    };
    if (isReady()) {
      resolve(true);
      return;
    }
    let unsubscribe: () => void = () => {};
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(isReady());
    }, SESSION_WAIT_TIMEOUT_MS);
    unsubscribe = useSessionStore.subscribe(() => {
      if (isReady()) {
        clearTimeout(timer);
        unsubscribe();
        resolve(true);
      }
    });
  });

const readMediaDimensions = (
  file: File,
  type: string,
): Promise<{ width?: number; height?: number; durationMs?: number }> =>
  new Promise((resolve) => {
    let objectUrl: string | undefined;
    let settled = false;
    const finish = (value: {
      width?: number;
      height?: number;
      durationMs?: number;
    }) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      resolve(value);
    };
    // Metadata is a nice-to-have for the request — never hold the send on it.
    const timer = setTimeout(() => finish({}), 1500);
    try {
      objectUrl = URL.createObjectURL(file);
      if (type.startsWith("image/")) {
        const image = new Image();
        image.onload = () =>
          finish({
            width: image.naturalWidth || undefined,
            height: image.naturalHeight || undefined,
          });
        image.onerror = () => finish({});
        image.src = objectUrl;
        return;
      }
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.onloadedmetadata = () =>
        finish({
          width: video.videoWidth || undefined,
          height: video.videoHeight || undefined,
          durationMs: isFinite(video.duration)
            ? Math.round(video.duration * 1000)
            : undefined,
        });
      video.onerror = () => finish({});
      video.src = objectUrl;
    } catch (_) {
      finish({});
    }
  });

const pickBoolean = (...values: any[]): boolean | undefined =>
  values.find((value) => typeof value === "boolean");
const pickNumber = (...values: any[]): number | undefined =>
  values.find((value) => typeof value === "number" && isFinite(value));

const sanitizeCapabilities = (data: any): CaptureHostCapabilities => ({
  version: typeof data.version === "number" ? data.version : undefined,
  platform: ["web", "ios", "android"].includes(data.platform)
    ? data.platform
    : undefined,
  sdkType:
    typeof data.sdkType === "string" ? data.sdkType.slice(0, 40) : undefined,
  sdkVersion:
    typeof data.sdkVersion === "string"
      ? data.sdkVersion.slice(0, 40)
      : undefined,
  screenshot: data.screenshot === true,
  recording: data.recording === true,
  recordingMethod: ["display", "rrweb", "frames"].includes(
    data.recordingMethod,
  )
    ? data.recordingMethod
    : undefined,
  annotate: data.annotate !== false,
  maxRecordingSec:
    typeof data.maxRecordingSec === "number" ? data.maxRecordingSec : undefined,
  microphone: data.microphone === true,
  pause: data.pause === true,
  flyIn: data.flyIn === true,
});

type ErrorOutcome = "final" | "fatal" | "transient";

export const useCaptureStore = create<CaptureState>()((set, get) => {
  const patchRequest = (
    requestId: string,
    patch: Partial<CaptureRequestLocalState>,
  ) => {
    set((state) => {
      const previous: CaptureRequestLocalState | undefined =
        state.requests[requestId];
      return {
        requests: {
          ...state.requests,
          [requestId]: { ...(previous ?? { phase: "idle" }), ...patch },
        },
      };
    });
  };

  const unwatchVisibility = () => {
    clearHostSilenceTimer();
    if (visibilityListener && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", visibilityListener);
    }
    visibilityListener = undefined;
  };

  const releaseActive = (requestId: string) => {
    clearHostWatchdog(requestId);
    if (get().activeRequestId === requestId) {
      set({ activeRequestId: undefined });
      unwatchVisibility();
    }
  };

  // Server truth for a request the host went quiet about. A final status
  // settles the card (completed → "Sent", …) instead of a "failed" for work
  // that actually reached the server. Still open: only the first-response
  // watchdog (`failIfOpen`) treats that as a lost capture; otherwise the host
  // may legitimately still be busy (the customer sits in its bar) and the
  // card keeps waiting, with its Cancel.
  const reconcileWithServer = async (
    requestId: string,
    failIfOpen: boolean,
  ) => {
    let status: string | undefined;
    try {
      const request = await HttpCaptureService.getInstance().getRequest(
        requestId,
      );
      status = typeof request?.status === "string" ? request.status : undefined;
    } catch (_) {}

    const local = get().requests[requestId];
    // The host spoke (or the customer acted) while we were asking.
    if (local?.phase !== "host" || (failIfOpen && local.hostState)) {
      return;
    }
    if (isFinalCaptureStatus(status)) {
      patchRequest(requestId, {
        phase:
          status === "completed"
            ? "done"
            : status === "skipped"
              ? "skipped"
              : "idle",
        finalStatus: status as CaptureStatus,
        hostState: undefined,
        busyAction: undefined,
        issue: undefined,
        progress: undefined,
        sentKind: local.sentKind ?? local.activeKind,
      });
      releaseActive(requestId);
      refreshConversationSoon();
      return;
    }
    if (!failIfOpen) {
      return;
    }
    Communicator.captureCancel(requestId);
    patchRequest(requestId, {
      phase: "idle",
      hostState: undefined,
      issue: "failed",
    });
    releaseActive(requestId);
    reportEvent(requestId, "failed", "host-no-response");
  };

  const armHostSilenceCheck = (requestId: string) => {
    clearHostSilenceTimer();
    if (isPageHidden()) {
      // Re-armed by the visibility listener once the page is back.
      return;
    }
    hostSilenceTimer = setTimeout(() => {
      hostSilenceTimer = undefined;
      if (isPageHidden() || get().requests[requestId]?.phase !== "host") {
        return;
      }
      reconcileWithServer(requestId, false);
    }, HOST_SILENCE_CHECK_MS);
  };

  const watchVisibility = () => {
    if (visibilityListener || typeof document === "undefined") {
      return;
    }
    visibilityListener = () => {
      const activeId = get().activeRequestId;
      if (!activeId || get().requests[activeId]?.phase !== "host") {
        return;
      }
      if (isPageHidden()) {
        clearHostSilenceTimer();
        return;
      }
      // Back from the background (or a suspended WebView): give the host's
      // queued messages a moment, then ask the server.
      armHostSilenceCheck(activeId);
    };
    document.addEventListener("visibilitychange", visibilityListener);
  };

  const closeEditor = () => {
    const editor = get().editor;
    if (!editor) {
      return;
    }
    set({ editor: undefined });
    Communicator.captureEditor(editor.requestId, false);
  };

  const platformInfo = () => {
    const capabilities = get().hostCapabilities;
    return {
      platform: detectCapturePlatform({
        capabilities,
        isApp: useConfigStore.getState().isApp,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      }),
      ...(capabilities?.sdkType ? { sdkType: capabilities.sdkType } : {}),
      ...(capabilities?.sdkVersion
        ? { sdkVersion: capabilities.sdkVersion }
        : {}),
    };
  };

  const claimBody = (method?: string) => ({
    deviceId: getCaptureDeviceId(),
    ...platformInfo(),
    ...(method ? { method } : {}),
  });

  // A 410 means the request is final server-side; the card shows which final
  // state as soon as we know it (the comment update may lag behind).
  const learnFinalStatus = async (requestId: string, error: any) => {
    let status =
      error?.data?.status ?? error?.data?.captureRequest?.status ?? undefined;
    if (!isFinalCaptureStatus(status)) {
      try {
        const request = await HttpCaptureService.getInstance().getRequest(
          requestId,
        );
        status = request?.status;
      } catch (_) {}
    }
    patchRequest(requestId, {
      finalStatus: isFinalCaptureStatus(status) ? status : "expired",
    });
  };

  const classifyError = async (
    requestId: string,
    error: any,
  ): Promise<ErrorOutcome> => {
    const status = error?.status;
    if (status === 410) {
      await learnFinalStatus(requestId, error);
      return "final";
    }
    if (!status || status >= 500 || status === 408 || status === 429) {
      return "transient";
    }
    return "fatal";
  };

  // /complete is first-wins and idempotent for the same files, so one repeat
  // after a timeout / network drop is safe (it answers 200 with the stored
  // result). A different result already stored answers 409.
  const completeRequest = async (
    requestId: string,
    body: Record<string, any>,
  ): Promise<"completed" | "final" | "error"> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await HttpCaptureService.getInstance().complete(requestId, body);
        return "completed";
      } catch (error: any) {
        if (error?.status === 409) {
          // Usually another device answered first, but an older server also
          // answers 409 for a closed conversation. "Sent" only when the server
          // says so; otherwise the capture stays here with an issue.
          let serverStatus: any;
          try {
            serverStatus = (
              await HttpCaptureService.getInstance().getRequest(requestId)
            )?.status;
          } catch (_) {}
          if (isFinalCaptureStatus(serverStatus)) {
            patchRequest(requestId, { finalStatus: serverStatus });
            return "final";
          }
          return "error";
        }
        if (error?.status === 410) {
          await learnFinalStatus(requestId, error);
          return "final";
        }
        if (!error?.status && attempt === 0) {
          continue;
        }
        return "error";
      }
    }
    return "error";
  };

  const hostOptions = (requestId: string, serverRequest?: any) => {
    const meta = get().meta[requestId];
    const options = {
      ...(meta?.options ?? {}),
      ...(serverRequest?.options && typeof serverRequest.options === "object"
        ? serverRequest.options
        : {}),
    };
    // The request's limit, else the default — never more than the host can
    // record (its `maxRecordingSec` is a ceiling, not a default).
    const maxDurationSec =
      pickNumber(meta?.maxDurationSec, options.maxDurationSec) ??
      DEFAULT_CAPTURE_MAX_RECORDING_SEC;
    const hostMax =
      pickNumber(get().hostCapabilities?.maxRecordingSec) ?? MAX_RECORDING_SEC;
    return {
      annotate: pickBoolean(meta?.annotate, options.annotate) ?? true,
      maxDurationSec: Math.min(
        MAX_RECORDING_SEC,
        Math.max(5, hostMax),
        Math.max(5, Math.round(maxDurationSec)),
      ),
      audio: pickBoolean(meta?.audio, options.audio) ?? false,
      attachLogs: pickBoolean(options.attachLogs) ?? true,
      include: {
        ...DEFAULT_CAPTURE_INCLUDE,
        ...(options.include && typeof options.include === "object"
          ? options.include
          : {}),
      },
    };
  };

  const shareTokenFor = (requestId: string, explicit?: string) =>
    explicit ??
    get().meta[requestId]?.ticketShareToken ??
    useTicketStore.getState().currentTicket?.shareToken;

  const armHostWatchdog = (requestId: string) => {
    clearHostWatchdog();
    const armedAt = Date.now();
    hostWatchdog = {
      requestId,
      armedAt,
      timer: setTimeout(() => {
        hostWatchdog = undefined;
        const local = get().requests[requestId];
        if (local?.phase !== "host" || local.hostState) {
          return;
        }
        // Hidden, or frozen for a while (suspended WebView / background
        // tab): the host's silence means nothing yet — check once the page
        // is back instead of failing a capture that may have completed.
        const wasSuspended =
          Date.now() - armedAt >
          HOST_FIRST_RESPONSE_TIMEOUT_MS + TIMER_SUSPENSION_SLACK_MS;
        if (isPageHidden()) {
          watchVisibility();
          return;
        }
        reconcileWithServer(requestId, !wasSuspended);
      }, HOST_FIRST_RESPONSE_TIMEOUT_MS),
    };
  };

  const sendCaptureStart = (
    requestId: string,
    kind: CaptureMediaKind,
    serverRequest?: any,
    ticketShareToken?: string,
  ) => {
    set({ activeRequestId: requestId });
    Communicator.captureStart({
      requestId,
      kind,
      ticketShareToken: shareTokenFor(requestId, ticketShareToken),
      options: hostOptions(requestId, serverRequest),
      labels: buildCaptureHostLabels(),
    });
    armHostWatchdog(requestId);
    watchVisibility();
  };

  const openEditor = (requestId: string, data: any) => {
    const meta = get().meta[requestId];
    const previous = get().editor;
    if (previous && previous.requestId !== requestId) {
      // A second image for another request replaces the open one.
      Communicator.captureCancel(previous.requestId);
      patchRequest(previous.requestId, { phase: "idle" });
      reportEvent(previous.requestId, "released");
    }
    editorSessionCounter += 1;
    set({
      activeRequestId: requestId,
      editor: {
        sessionId: editorSessionCounter,
        requestId,
        dataUrl: data.dataUrl,
        width: typeof data.width === "number" ? data.width : undefined,
        height: typeof data.height === "number" ? data.height : undefined,
        annotate: meta?.annotate !== false,
        canRetake: get().hostCapabilities?.screenshot === true,
        method: typeof data.method === "string" ? data.method : undefined,
        platform: typeof data.platform === "string" ? data.platform : undefined,
        sdkType: typeof data.sdkType === "string" ? data.sdkType : undefined,
        sdkVersion:
          typeof data.sdkVersion === "string" ? data.sdkVersion : undefined,
        phase: "editing",
      },
    });
    patchRequest(requestId, {
      phase: "editing",
      hostState: undefined,
      busyAction: undefined,
      issue: undefined,
      activeKind: "screenshot",
    });
    Communicator.captureEditor(requestId, true);
  };

  // capture-image for a request this page never started: the widget was
  // re-created mid-capture (Android). Load it, open its conversation, then
  // the editor.
  const loadRequestForImage = async (
    requestId: string,
  ): Promise<CaptureRequestMeta | undefined> => {
    await waitForSession();
    const request = await HttpCaptureService.getInstance().getRequest(
      requestId,
    );
    const card = parseCaptureCardData(request);
    if (!card || isFinalCaptureStatus(card.status)) {
      return undefined;
    }
    const options =
      request?.options && typeof request.options === "object"
        ? request.options
        : {};
    const meta: CaptureRequestMeta = {
      id: requestId,
      kind: card.kind,
      allowUpload: card.allowUpload ?? pickBoolean(options.allowUpload),
      annotate: card.annotate ?? pickBoolean(options.annotate),
      maxDurationSec: card.maxDurationSec ?? pickNumber(options.maxDurationSec),
      audio: card.audio ?? pickBoolean(options.audio),
      options,
      ticketShareToken:
        typeof request?.ticketShareToken === "string"
          ? request.ticketShareToken
          : undefined,
    };
    get().registerRequest(meta);

    const shareToken = meta.ticketShareToken;
    const router = useRouterStore.getState();
    if (
      shareToken &&
      !(
        router.currentPage === "conversation" &&
        router.currentData?.shareToken === shareToken
      )
    ) {
      router.pushPage("conversation", { shareToken });
    }
    return meta;
  };

  return {
    hostCapabilities: undefined,
    legacyHost: false,
    requests: {},
    meta: {},
    activeRequestId: undefined,
    editor: undefined,

    setHostScreenshot: (data) => {
      // Native sends the data URL itself; tolerate `{ dataUrl }` too.
      const dataUrl =
        typeof data === "string" ? data : data?.dataUrl ?? data?.data;
      if (
        isChatbar() ||
        !isCaptureImageDataUrl(dataUrl) ||
        dataUrl.length > MAX_CAPTURE_DATA_URL_LENGTH
      ) {
        return;
      }
      set({ hostScreenshot: { dataUrl, at: Date.now() } });
    },

    sendHostScreenshot: async (card, ticketShareToken) => {
      const shot = get().hostScreenshot;
      if (!shot) {
        return;
      }
      let file: File;
      try {
        const blob = await (await fetch(shot.dataUrl)).blob();
        const type = blob.type || "image/png";
        file = new File([blob], `screenshot.${type.split("/")[1] || "png"}`, {
          type,
        });
      } catch (_) {
        patchRequest(card.id, { issue: "failed" });
        return;
      }
      await get().uploadCaptureFile(card, file, ticketShareToken);
      if (get().requests[card.id]?.phase === "done") {
        // Shown like a capture (the image), not as a picked file's row.
        patchRequest(card.id, {
          fileName: undefined,
          fileSize: undefined,
          sentVia: "capture",
          annotated: false,
        });
      }
    },

    editHostScreenshot: async (card, ticketShareToken) => {
      const shot = get().hostScreenshot;
      const requestId = card.id;
      const local = get().requests[requestId];
      if (!shot || (local && local.phase !== "idle")) {
        return;
      }
      const active = get().activeRequestId;
      if (active && active !== requestId) {
        return;
      }
      get().registerRequest({
        id: requestId,
        kind: card.kind,
        allowUpload: card.allowUpload,
        annotate: card.annotate,
        ticketShareToken: shareTokenFor(requestId, ticketShareToken),
      });
      patchRequest(requestId, {
        phase: "claiming",
        busyAction: "screenshot",
        issue: undefined,
      });
      try {
        await HttpCaptureService.getInstance().claim(
          requestId,
          claimBody("upload"),
        );
      } catch (error) {
        const outcome = await classifyError(requestId, error);
        if (outcome !== "transient") {
          patchRequest(requestId, {
            phase: "idle",
            busyAction: undefined,
            issue: outcome === "fatal" ? "failed" : undefined,
          });
          return;
        }
      }
      if (get().requests[requestId]?.phase !== "claiming") {
        return;
      }
      // The editor sends it through /uploads + /complete as `upload` (§3.2).
      openEditor(requestId, { dataUrl: shot.dataUrl, method: "upload" });
    },

    applyCapabilities: (data) => {
      // Only the widget frame captures; the chatbar stays upload-only.
      if (isChatbar() || !data || typeof data !== "object") {
        return;
      }
      set({ hostCapabilities: sanitizeCapabilities(data), legacyHost: false });
    },

    markLegacyIfUnannounced: () => {
      const { hostCapabilities, legacyHost } = get();
      if (hostCapabilities || legacyHost) {
        return 0;
      }
      const remaining = CAPTURE_CAPABILITIES_GRACE_MS - (Date.now() - bootedAt);
      if (remaining <= 0) {
        set({ legacyHost: true });
        return 0;
      }
      return remaining;
    },

    registerRequest: (meta) => {
      if (!meta?.id) {
        return;
      }
      set((state) => {
        const existing = state.meta[meta.id];
        return {
          meta: {
            ...state.meta,
            [meta.id]: {
              ...existing,
              ...meta,
              options: meta.options ?? existing?.options,
              ticketShareToken:
                meta.ticketShareToken ?? existing?.ticketShareToken,
            },
          },
        };
      });
    },

    applyHostState: (data) => {
      const requestId = data?.requestId;
      const state = data?.state as CaptureHostStateName;
      if (
        typeof requestId !== "string" ||
        !requestId ||
        !CAPTURE_HOST_STATES.includes(state) ||
        isChatbar()
      ) {
        return;
      }
      const local = get().requests[requestId];
      // Settled here already — a late host message must not reopen it.
      if (local?.phase === "done" || local?.phase === "skipped") {
        return;
      }
      clearHostWatchdog(requestId);

      switch (state) {
        case "bar":
        case "capturing":
        case "recording":
        case "preview":
        case "uploading": {
          const hasElapsed =
            typeof data.elapsedSec === "number" && isFinite(data.elapsedSec);
          patchRequest(requestId, {
            phase: "host",
            busyAction: undefined,
            hostState: state,
            issue: undefined,
            progress:
              typeof data.progress === "number"
                ? Math.min(1, Math.max(0, data.progress))
                : state === "uploading"
                  ? local?.progress
                  : undefined,
            elapsedSec: hasElapsed
              ? Math.max(0, data.elapsedSec)
              : state === "recording"
                ? local?.elapsedSec
                : undefined,
            elapsedAt: hasElapsed
              ? Date.now()
              : state === "recording"
                ? local?.elapsedAt
                : undefined,
            paused: state === "recording" && data.paused === true,
          });
          set({ activeRequestId: requestId });
          // Every host message restarts the silence check; a host that keeps
          // talking (recording ticks, upload progress) never triggers it.
          watchVisibility();
          armHostSilenceCheck(requestId);
          return;
        }
        case "done": {
          // The host completed the request itself (recordings upload and
          // /complete on the host side). Capture UI v2 hosts add what the
          // card shows: a poster frame, the length and whether it has sound.
          const thumbnail =
            isCaptureImageDataUrl(data.thumbnail) &&
            data.thumbnail.length <= MAX_HOST_THUMBNAIL_LENGTH
              ? data.thumbnail
              : undefined;
          const flies =
            get().hostCapabilities?.flyIn === true && !prefersReducedMotion();
          patchRequest(requestId, {
            phase: "done",
            hostState: "done",
            progress: 1,
            paused: false,
            sentKind: local?.activeKind ?? "recording",
            sentVia: "capture",
            sentAt: Date.now(),
            thumbnailUrl: thumbnail ?? local?.thumbnailUrl,
            durationSec:
              typeof data.durationSec === "number" &&
              isFinite(data.durationSec) &&
              data.durationSec >= 0
                ? Math.round(data.durationSec)
                : undefined,
            audio: typeof data.audio === "boolean" ? data.audio : undefined,
            // The host flies its preview into the card; without a thumbnail
            // there is no media slot to land in.
            flyPending: flies && !!thumbnail,
            flyFrom: undefined,
            flySrc: undefined,
          });
          releaseActive(requestId);
          if (get().editor?.requestId === requestId) {
            closeEditor();
          }
          refreshConversationSoon();
          return;
        }
        default: {
          // cancelled / failed / declined / unsupported: back to the choices,
          // with the reason (and the upload) when something went wrong.
          const issue =
            state === "failed"
              ? "failed"
              : state === "declined"
                ? "permissionDenied"
                : state === "unsupported"
                  ? "notSupported"
                  : undefined;
          const unsupportedKinds =
            state === "unsupported" && local?.activeKind
              ? Array.from(
                  new Set([
                    ...(local?.unsupportedKinds ?? []),
                    local.activeKind,
                  ]),
                )
              : local?.unsupportedKinds;
          patchRequest(requestId, {
            phase: "idle",
            hostState: undefined,
            busyAction: undefined,
            progress: undefined,
            elapsedSec: undefined,
            elapsedAt: undefined,
            issue,
            unsupportedKinds,
          });
          releaseActive(requestId);
          if (get().editor?.requestId === requestId) {
            closeEditor();
          }
        }
      }
    },

    handleCaptureImage: (data) => {
      const requestId = data?.requestId;
      if (
        isChatbar() ||
        typeof requestId !== "string" ||
        !requestId ||
        !isCaptureImageDataUrl(data?.dataUrl) ||
        data.dataUrl.length > MAX_CAPTURE_DATA_URL_LENGTH
      ) {
        return;
      }
      clearHostWatchdog(requestId);
      clearHostSilenceTimer();
      const local = get().requests[requestId];
      if (
        local?.phase === "done" ||
        local?.phase === "skipped" ||
        isFinalCaptureStatus(local?.finalStatus)
      ) {
        return;
      }
      if (get().meta[requestId]) {
        openEditor(requestId, data);
        return;
      }
      loadRequestForImage(requestId)
        .then((meta) => {
          if (meta) {
            openEditor(requestId, data);
          }
        })
        .catch(() => {});
    },

    startCapture: async (card, kind, ticketShareToken) => {
      const requestId = card.id;
      const local = get().requests[requestId];
      if (local && local.phase !== "idle") {
        return;
      }
      const active = get().activeRequestId;
      if (active && active !== requestId) {
        return;
      }
      get().registerRequest({
        id: requestId,
        kind: card.kind,
        allowUpload: card.allowUpload,
        annotate: card.annotate,
        maxDurationSec: card.maxDurationSec,
        audio: card.audio,
        ticketShareToken: shareTokenFor(requestId, ticketShareToken),
      });
      patchRequest(requestId, {
        phase: "claiming",
        busyAction: kind,
        issue: undefined,
        claimedElsewhere: false,
        hostState: undefined,
        activeKind: kind,
      });
      set({ activeRequestId: requestId });

      let claim: CaptureClaimResponse | undefined;
      try {
        const method =
          kind === "recording"
            ? get().hostCapabilities?.recordingMethod
            : undefined;
        claim = await HttpCaptureService.getInstance().claim(
          requestId,
          claimBody(method),
        );
      } catch (error) {
        const outcome = await classifyError(requestId, error);
        if (outcome !== "transient") {
          patchRequest(requestId, {
            phase: "idle",
            busyAction: undefined,
            issue: outcome === "fatal" ? "failed" : undefined,
          });
          releaseActive(requestId);
          return;
        }
        // The claim is soft (it never blocks) — a flaky network must not stop
        // the customer from capturing; /complete settles it later.
      }

      // Reset (session change) or answered elsewhere while claiming.
      if (get().requests[requestId]?.phase !== "claiming") {
        releaseActive(requestId);
        return;
      }
      patchRequest(requestId, {
        phase: "host",
        busyAction: undefined,
        claimedElsewhere: claim?.claimedElsewhere === true,
      });
      sendCaptureStart(
        requestId,
        kind,
        claim?.captureRequest,
        ticketShareToken,
      );
    },

    cancelCapture: (requestId) => {
      const local = get().requests[requestId];
      if (local?.phase !== "host") {
        return;
      }
      Communicator.captureCancel(requestId);
      patchRequest(requestId, {
        phase: "idle",
        hostState: undefined,
        progress: undefined,
        elapsedSec: undefined,
        elapsedAt: undefined,
      });
      releaseActive(requestId);
      reportEvent(requestId, "released");
    },

    uploadCaptureFile: async (card, file, ticketShareToken) => {
      const requestId = card.id;
      const local = get().requests[requestId];
      if (local && local.phase !== "idle") {
        return;
      }
      const type = captureFileMimeType(file);
      if (!type) {
        patchRequest(requestId, { issue: "failed" });
        return;
      }
      if (file.size > CAPTURE_UPLOAD_LIMIT_BYTES) {
        patchRequest(requestId, { issue: "uploadTooLarge" });
        return;
      }
      get().registerRequest({
        id: requestId,
        kind: card.kind,
        allowUpload: card.allowUpload,
        annotate: card.annotate,
        ticketShareToken: shareTokenFor(requestId, ticketShareToken),
      });
      patchRequest(requestId, {
        phase: "claiming",
        busyAction: "upload",
        issue: undefined,
        claimedElsewhere: false,
      });

      let claim: CaptureClaimResponse | undefined;
      try {
        claim = await HttpCaptureService.getInstance().claim(
          requestId,
          claimBody("upload"),
        );
      } catch (error) {
        const outcome = await classifyError(requestId, error);
        if (outcome !== "transient") {
          patchRequest(requestId, {
            phase: "idle",
            busyAction: undefined,
            issue: outcome === "fatal" ? "failed" : undefined,
          });
          return;
        }
      }
      if (get().requests[requestId]?.phase !== "claiming") {
        return;
      }
      patchRequest(requestId, {
        phase: "uploading",
        busyAction: undefined,
        progress: 0,
        claimedElsewhere: claim?.claimedElsewhere === true,
        fileName: file.name || undefined,
        fileSize: file.size,
      });

      let url: string;
      try {
        url = await useTicketStore.getState().uploadFile(
          file,
          (pct) => {
            if (get().requests[requestId]?.phase === "uploading") {
              patchRequest(requestId, { progress: Math.min(1, pct / 100) });
            }
          },
          { maxBytes: CAPTURE_UPLOAD_LIMIT_BYTES },
        );
      } catch (_) {
        patchRequest(requestId, {
          phase: "idle",
          issue: "failed",
          fileName: undefined,
          fileSize: undefined,
        });
        reportEvent(requestId, "failed", "upload");
        return;
      }

      const dimensions = await readMediaDimensions(file, type);
      patchRequest(requestId, { phase: "completing", progress: 1 });
      const isVideo = type.startsWith("video/");
      const outcome = await completeRequest(requestId, {
        files: [
          {
            url,
            name:
              file.name ||
              (isVideo ? "screen-recording" : "screenshot") +
                (type.includes("/") ? `.${type.split("/")[1]}` : ""),
            type,
            size: file.size,
            ...dimensions,
          },
        ],
        method: "upload",
        ...claimBody(),
      });
      if (outcome === "completed") {
        patchRequest(requestId, {
          phase: "done",
          progress: 1,
          thumbnailUrl: isVideo ? undefined : trackObjectUrl(file),
          sentKind: isVideo ? "recording" : "screenshot",
          sentVia: "upload",
          sentAt: Date.now(),
          fileName: file.name || undefined,
          fileSize: file.size,
        });
        Communicator.captureDone(requestId, false);
        refreshConversationSoon();
        return;
      }
      patchRequest(requestId, {
        phase: "idle",
        progress: undefined,
        fileName: undefined,
        fileSize: undefined,
        issue: outcome === "error" ? "failed" : undefined,
      });
      if (outcome === "error") {
        reportEvent(requestId, "failed", "complete");
      }
    },

    skipRequest: async (card) => {
      const requestId = card.id;
      const local = get().requests[requestId];
      if (local && local.phase !== "idle") {
        return;
      }
      patchRequest(requestId, {
        phase: "skipping",
        busyAction: "skip",
        issue: undefined,
      });
      try {
        await HttpCaptureService.getInstance().skip(
          requestId,
          captureText("captureSkipMessage"),
        );
        patchRequest(requestId, { phase: "skipped", busyAction: undefined });
        releaseActive(requestId);
        Communicator.captureDone(requestId, false);
        refreshConversationSoon();
      } catch (error: any) {
        if (error?.status === 410) {
          await learnFinalStatus(requestId, error);
          patchRequest(requestId, { phase: "idle", busyAction: undefined });
          return;
        }
        patchRequest(requestId, {
          phase: "idle",
          busyAction: undefined,
          issue: "failed",
        });
      }
    },

    sendEditorResult: async (result) => {
      const editor = get().editor;
      if (!editor || editor.phase === "sending") {
        return;
      }
      const { requestId, sessionId } = editor;
      const isCurrentSession = () => get().editor?.sessionId === sessionId;
      const setEditor = (patch: Partial<CaptureEditorSession>) => {
        const current = get().editor;
        if (current && current.sessionId === sessionId) {
          set({ editor: { ...current, ...patch } });
        }
      };

      setEditor({ phase: "sending", progress: 0 });
      patchRequest(requestId, { phase: "uploading", progress: 0 });

      const fileName = `screenshot-${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.jpg`;
      let url: string;
      try {
        const file = new File([result.blob], fileName, { type: "image/jpeg" });
        url = await useTicketStore.getState().uploadFile(
          file,
          (pct) => {
            setEditor({ progress: Math.min(1, pct / 100) });
            patchRequest(requestId, { progress: Math.min(1, pct / 100) });
          },
          { maxBytes: CAPTURE_UPLOAD_LIMIT_BYTES },
        );
      } catch (_) {
        setEditor({ phase: "error", progress: undefined });
        patchRequest(requestId, { phase: "editing" });
        return;
      }

      patchRequest(requestId, { phase: "completing", progress: 1 });
      const platform = editor.platform ?? platformInfo().platform;
      const outcome = await completeRequest(requestId, {
        files: [
          {
            url,
            name: fileName,
            type: "image/jpeg",
            size: result.blob.size,
            width: result.width,
            height: result.height,
          },
        ],
        method: editor.method ?? (platform === "web" ? "dom" : "frames"),
        ...claimBody(),
        platform,
        ...(editor.sdkType ? { sdkType: editor.sdkType } : {}),
        ...(editor.sdkVersion ? { sdkVersion: editor.sdkVersion } : {}),
        annotations: result.annotations,
      });

      if (outcome === "error") {
        setEditor({ phase: "error", progress: undefined });
        patchRequest(requestId, { phase: "editing" });
        return;
      }
      // Where the screenshot sat in the (full-viewport) editor: the flight
      // into the card starts there. Read before the editor closes.
      const flyFrom = getCaptureEditorImageRect();
      const flies =
        outcome === "completed" &&
        get().hostCapabilities?.flyIn === true &&
        !!flyFrom &&
        !prefersReducedMotion();
      const flySrc = flies ? await makeCaptureFlightImage(result.blob) : undefined;
      if (isCurrentSession()) {
        closeEditor();
      }
      patchRequest(requestId, {
        phase: outcome === "completed" ? "done" : "idle",
        progress: undefined,
        ...(outcome === "completed"
          ? {
              thumbnailUrl: trackObjectUrl(result.blob),
              sentKind: "screenshot",
              sentVia: "capture",
              sentAt: Date.now(),
              annotated: result.annotations.length > 0,
              flyPending: !!flySrc,
              flyFrom: flySrc ? flyFrom : undefined,
              flySrc,
            }
          : {}),
      });
      releaseActive(requestId);
      Communicator.captureDone(requestId, outcome === "completed");
      refreshConversationSoon();
    },

    retakeFromEditor: () => {
      const editor = get().editor;
      if (!editor || editor.phase === "sending" || !editor.canRetake) {
        return;
      }
      const { requestId } = editor;
      closeEditor();
      patchRequest(requestId, {
        phase: "host",
        hostState: undefined,
        activeKind: "screenshot",
        issue: undefined,
      });
      sendCaptureStart(requestId, "screenshot");
    },

    cancelEditor: () => {
      const editor = get().editor;
      if (!editor || editor.phase === "sending") {
        return;
      }
      const { requestId } = editor;
      closeEditor();
      Communicator.captureCancel(requestId);
      patchRequest(requestId, {
        phase: "idle",
        hostState: undefined,
        busyAction: undefined,
      });
      releaseActive(requestId);
      reportEvent(requestId, "released");
    },

    finishFlight: (requestId) => {
      if (typeof requestId !== "string" || !get().requests[requestId]) {
        return;
      }
      if (!get().requests[requestId].flyPending) {
        return;
      }
      patchRequest(requestId, {
        flyPending: false,
        flyFrom: undefined,
        flySrc: undefined,
      });
    },

    releaseStaleClaim: (card) => {
      if (!card?.id || seenCaptureCards.has(card.id)) {
        return;
      }
      seenCaptureCards.add(card.id);
      if (card.status !== "in_progress") {
        return;
      }
      const { activeRequestId, requests } = get();
      if (
        activeRequestId === card.id ||
        (requests[card.id]?.phase ?? "idle") !== "idle"
      ) {
        return;
      }
      reportEvent(card.id, "released", "stale");
    },

    reset: () => {
      clearHostWatchdog();
      unwatchVisibility();
      const { editor, activeRequestId } = get();
      if (editor) {
        Communicator.captureEditor(editor.requestId, false);
      }
      if (activeRequestId) {
        Communicator.captureCancel(activeRequestId);
      }
      trackedObjectUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {}
      });
      trackedObjectUrls.clear();
      // Capabilities describe the host, not the user — they survive. The
      // host screenshot showed the previous user's screen: it goes.
      set({
        hostScreenshot: undefined,
        requests: {},
        meta: {},
        activeRequestId: undefined,
        editor: undefined,
      });
    },
  };
});

// The customer closed the page mid-capture: release the claim so the request
// doesn't read "in progress" (dashboard, other devices) until it expires. The
// claim is soft, so this never stops a later answer.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    try {
      const { activeRequestId, requests } = useCaptureStore.getState();
      const phase = activeRequestId ? requests[activeRequestId]?.phase : undefined;
      if (
        activeRequestId &&
        (phase === "claiming" || phase === "host" || phase === "editing")
      ) {
        HttpCaptureService.getInstance().releaseKeepalive(activeRequestId);
      }
    } catch (_) {}
  });
}
